/**
 * ContextPreviewPanel — read-only "see context exactly as the LLM uses it".
 *
 * Replaces the old per-pool upload drawer (uploads now live in the Context tab).
 * Renders the assembled context for this room's (networkId, poolId, agentId),
 * grouped by the tag (scope) it was applied at:
 *
 *   @Org            <org RULES text, verbatim>
 *   @Network        <network RULES text, verbatim>
 *   @Network Pool   <pool RULES text, verbatim>
 *   …
 *   [REFERENCE — retrieved at runtime from the vector DB]  • doc1, doc2 …
 *
 * RULES are shown VERBATIM and are produced by the SAME backend merge the worker
 * runs (faithful, no drift). REFERENCE is a placeholder (retrieval is
 * query-dependent at runtime) + the mounted filenames + an optional sample chunk
 * from the vector store (labelled a sample, NOT the runtime result).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { useContextChannel } from '@stepflow/ui-shared/hooks/useContextChannel';
import { getRestApiBearerToken, tenantIdFromBearerToken } from '@stepflow/ui-shared/services/apiToken';
import {
  getContextPreview,
  runContextRetrieval,
  getRoomContext,
  postRoomContext,
} from '@stepflow/ui-shared/services/dataPlaneApi';
import { listAgentConnectorOverrides } from '@stepflow/ui-shared/services/poolMembersApi.js';
import { targetClassName, targetLabel } from '@stepflow/ui-shared/components/dataPlane/MountTarget';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import PromptComposer from '@stepflow/ui-shared/components/PromptComposer.jsx';
import ContextLevelSection from './ContextLevelSection.jsx';
import NetworkStateSection from './NetworkStateSection.jsx';

/**
 * A single collapsible document row inside a context level — header only until the user opens it,
 * then the body renders (lazy) via the shared Markdown viewer (no raw ``<pre>``). Used for RULES
 * docs + SKILLS so a level with "a lot" of items shows a compact header list, all collapsed.
 */
function DocDisclosure({ title, chip, meta, muted = false, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-md border border-gray-100 ${muted ? 'opacity-70' : ''}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-gray-50"
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
        {chip || null}
        <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-gray-800">{title}</span>
        {meta ? <span className="shrink-0 text-[10px] text-gray-400">{meta}</span> : null}
      </button>
      {open ? (
        <div className="max-h-[60vh] overflow-auto border-t border-gray-100 px-3 py-2 text-[12px] leading-relaxed text-gray-800">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Scope tag chip — the SAME coloured chip the Context catalog shows on hover /
 * assign (ORG=amber, NETWORK=blue, POOL=emerald, AGENT=violet, AGENT_POOL=
 * fuchsia), driven by the single source of truth ``targetClassName`` /
 * ``targetLabel`` in MountTarget.js. Used as each context section's header so the
 * user relates a RULES/REFERENCE block to the exact tag it was applied at.
 *
 * The backend preview already emits ``scope`` per block; we build a minimal
 * ``target`` from it (+ the panel's known ids) so the chip colour + label match
 * the catalog pixel-for-pixel. When a richer ``label`` (e.g. with pool/agent
 * names) is provided we show it after the chip for disambiguation.
 */
function ScopeTagChip({ scope, networkId, poolId, agentName }) {
  const target = { scope, networkId, poolId, agentName };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[11px] font-medium ${targetClassName(target)}`}
      title={targetLabel(target)}
    >
      <span className="truncate">{targetLabel(target)}</span>
    </span>
  );
}

/** Human date from an ISO-8601 string; '' on empty/garbage. */
function formatExtractedAt(iso) {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Room Context tab — the whole-room status backed by pool memory (the only
 * context dimension that accumulates across every call in a room). Shows the
 * thread roster + an on-demand synthesized narrative. Synthesis is a paid Haiku
 * call, so the flow is: Estimate (free) → confirm the points cost → Synthesize
 * (spends once) → cached with the extraction date. A staleness hint offers a
 * Refresh when new threads appeared since the last extract.
 */
function RoomContextTab({ open, networkId, poolId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estimate → confirm → synthesize.
  const [estimate, setEstimate] = useState(null);   // {threadsCovered, points, ...} once fetched
  const [estimating, setEstimating] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [actionError, setActionError] = useState(null);

  const refresh = useCallback(async () => {
    if (!networkId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await getRoomContext(networkId, { pool: poolId });
      setData(resp);
    } catch (e) {
      setError(e?.message || 'Failed to load room context');
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  useEffect(() => {
    if (open) {
      setEstimate(null);
      setActionError(null);
      refresh();
    }
  }, [open, refresh]);

  // §13 cross-room live reflect: a CONTEXT_CHANGED beacon (this room OR a sibling changed the SHARED
  // Lead-anchored context) → re-fetch while the drawer is open, so Lead + linked rooms show the SAME
  // context in real time. Bridged from the room's agent channel by AgenticNetworkRoom.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const onCtx = () => { refresh(); };
    window.addEventListener('stepflow-context-changed', onCtx);
    return () => window.removeEventListener('stepflow-context-changed', onCtx);
  }, [open, refresh]);

  const runEstimate = useCallback(async () => {
    if (!networkId) return;
    setEstimating(true);
    setActionError(null);
    try {
      const resp = await postRoomContext(networkId, { pool: poolId, action: 'estimate' });
      setEstimate(resp?.estimate || null);
    } catch (e) {
      setActionError(e?.message || 'Estimate failed');
    } finally {
      setEstimating(false);
    }
  }, [networkId, poolId]);

  const runSynthesize = useCallback(async () => {
    if (!networkId) return;
    setSynthesizing(true);
    setActionError(null);
    try {
      const resp = await postRoomContext(networkId, { pool: poolId, action: 'synthesize' });
      if (resp?.error) {
        setActionError(resp.error);
      } else {
        setEstimate(null);
        await refresh();
      }
    } catch (e) {
      setActionError(e?.message || 'Synthesis failed');
    } finally {
      setSynthesizing(false);
    }
  }, [networkId, poolId, refresh]);

  const threads = data?.threads || [];
  const summary = data?.summary || null;
  // §13 Model B — the Lead-anchored CONSOLIDATED roll-up of every room in the instance (same value
  // for the Lead + all linked rooms). {summary, extractedAt, rooms} or null.
  const groupSummary = data?.groupSummary || null;
  const newSince = data?.newThreadsSince || 0;
  const extractedAt = formatExtractedAt(summary?.extractedAt);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="room-context-tab">
      <p className="text-[11px] text-gray-500">
        The whole-room view, built from pool memory — the one context that carries
        across every call in this room. Each thread (ticket / topic) keeps its own
        rolling summary; the room status synthesises them on demand.
      </p>

      {loading ? (
        <div className="mt-3 text-xs text-gray-500">Loading room context…</div>
      ) : error ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">
          {error}
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          {/* Cached synthesis (or the empty / estimate-first state) */}
          <section data-testid="room-summary">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold text-teal-700">Room summary</h3>
              {summary ? (
                <span className="text-[10px] text-gray-400">
                  extracted {extractedAt}
                  {typeof summary.points === 'number' ? ` · ${summary.points} pts` : ''}
                  {summary.threadsCovered ? ` · ${summary.threadsCovered} threads` : ''}
                </span>
              ) : null}
            </div>

            {summary ? (
              <>
                <div className="mt-1 max-h-72 overflow-auto rounded-md bg-teal-50 px-3 py-2 text-[12px] leading-relaxed text-gray-800 ring-1 ring-teal-100">
                  <MarkdownRenderer content={summary.summary || ''} />
                </div>
                {newSince > 0 ? (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <span className="text-[11px] text-amber-700">
                      ⚠ {newSince} {newSince === 1 ? 'thread' : 'threads'} active since this summary.
                    </span>
                    {estimate ? (
                      <button
                        type="button"
                        onClick={runSynthesize}
                        disabled={synthesizing}
                        className="shrink-0 rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-50"
                        data-testid="room-refresh-confirm"
                      >
                        {synthesizing ? 'Refreshing…' : `Refresh — ~${estimate.points ?? '?'} pts`}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={runEstimate}
                        disabled={estimating}
                        className="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-300 transition-colors hover:bg-amber-100 disabled:opacity-50"
                        data-testid="room-refresh-estimate"
                      >
                        {estimating ? 'Estimating…' : 'Refresh…'}
                      </button>
                    )}
                  </div>
                ) : null}
              </>
            ) : threads.length === 0 ? (
              <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-gray-500">
                No room memory yet. As the room's agents work across runs, their
                cross-run memory accumulates here.
              </div>
            ) : (
              /* Threads exist but no synthesis yet — estimate-first. */
              <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                <p className="text-[11px] text-gray-600">
                  No room summary extracted yet. Synthesising one is a small one-time
                  cost; estimate it first.
                </p>
                {estimate ? (
                  <div className="mt-2 flex items-center justify-between gap-2" data-testid="room-estimate">
                    <span className="text-[11px] text-gray-600">
                      ~<span className="font-semibold">{estimate.points ?? '?'}</span> pts ·
                      {' '}{estimate.threadsCovered} threads
                      {estimate.executionsCovered ? ` · ${estimate.executionsCovered} runs` : ''}
                      {' '}· ~{estimate.inputTokens?.toLocaleString?.() ?? estimate.inputTokens} input tokens
                    </span>
                    <button
                      type="button"
                      onClick={runSynthesize}
                      disabled={synthesizing}
                      className="shrink-0 rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-50"
                      data-testid="room-synthesize-confirm"
                    >
                      {synthesizing ? 'Synthesising…' : 'Synthesise now'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={runEstimate}
                    disabled={estimating}
                    className="mt-2 rounded-md bg-violet-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-50"
                    data-testid="room-estimate-button"
                  >
                    {estimating ? 'Estimating…' : 'Estimate cost'}
                  </button>
                )}
              </div>
            )}

            {actionError ? (
              <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">
                {actionError}
              </div>
            ) : null}
          </section>

          {/* CONSOLIDATED group roll-up (§13 Model B) — the Lead's view of EVERY room in the
              instance (Lead + linked), so a room sees what its neighbours are doing ("logically one
              room"). Lead-anchored → identical for the Lead + all linked rooms. Shown only when
              some room in the group has activity. */}
          {groupSummary && (groupSummary.summary || '').trim() ? (
            <section data-testid="room-group-summary">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold text-fuchsia-700">Across the room group</h3>
                <span className="text-[10px] text-gray-400">
                  {(groupSummary.rooms || []).length} room{(groupSummary.rooms || []).length === 1 ? '' : 's'}
                </span>
              </div>
              <p className="mt-0.5 text-[10px] text-gray-400">
                The Lead’s consolidated view of every linked room — what’s happening next door.
              </p>
              <div className="mt-1 max-h-72 overflow-auto rounded-md bg-fuchsia-50 px-3 py-2 text-[12px] leading-relaxed text-gray-800 ring-1 ring-fuchsia-100">
                <MarkdownRenderer content={groupSummary.summary || ''} />
              </div>
            </section>
          ) : null}

          {/* Thread roster — always shown so the user sees what the room holds. */}
          <section data-testid="room-threads">
            <h3 className="text-xs font-semibold text-gray-700">
              Threads <span className="font-normal text-gray-400">({threads.length})</span>
            </h3>
            {threads.length === 0 ? (
              <p className="mt-1 text-[11px] text-gray-400">No threads in this room yet.</p>
            ) : (
              <ul className="mt-1 space-y-1.5">
                {threads.map((t) => (
                  <li key={t.threadId} className="rounded-md bg-gray-50 px-3 py-1.5 ring-1 ring-gray-100">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[12px] font-medium text-gray-800">{t.label || t.threadId}</span>
                      <span className="shrink-0 text-[10px] text-gray-400">
                        {t.episodes} {t.episodes === 1 ? 'run' : 'runs'}
                        {t.lastSeen ? ` · ${formatExtractedAt(t.lastSeen)}` : ''}
                      </span>
                    </div>
                    {(t.keywords || []).length ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.keywords.map((k) => (
                          <span key={k} className="rounded bg-gray-200 px-1.5 py-px text-[9px] font-medium text-gray-600">
                            {k}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Network state — what the network has actually produced (execution
              record + terminal-agent outcome). Independent of the pool-memory
              summary above; reads the existing executions API. */}
          <NetworkStateSection open={open} networkId={networkId} poolId={poolId} />
        </div>
      )}
    </div>
  );
}

export default function ContextPreviewPanel({
  open,
  onClose,
  networkId,
  poolId,
  agentId: agentIdProp,
  roomLabel,
}) {
  // Two tabs in the drawer: the existing per-agent "Context preview" and the
  // new whole-room "Room Context" status.
  const [tab, setTab] = useState('preview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Agent picker — mirrors the Settings drawer "Connectors" agent dropdown:
  // same source ([{agentId, agentLabel}] from the connector-overrides matrix),
  // so the preview is scoped to a chosen agent (retrieval is per-agent index).
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(agentIdProp || '');

  // Test-prompt + real vector retrieval (the data-quality harness).
  const [prompt, setPrompt] = useState('');
  const [retrieval, setRetrieval] = useState(null);
  const [retrieving, setRetrieving] = useState(false);
  const [retrievalError, setRetrievalError] = useState(null);

  const refresh = useCallback(async (agent) => {
    if (!networkId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await getContextPreview(networkId, { pool: poolId, agent: agent || undefined });
      setData(resp);
    } catch (e) {
      setError(e?.message || 'Failed to load context preview');
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  // §26.5 — LIVE auto-populate: when a consolidation tier fills (L1/L2 land after upload), the
  // backend emits CONTEXT_SYNC_STATUS {status:'FILLED'} on the tenant Context channel. Re-pull the
  // preview so a RULES/SKILLS block flips from the "processing…" shell to the real content without
  // the user closing + reopening the drawer. (useContextChannel keeps the latest callback.)
  const ctxTenant = useMemo(() => tenantIdFromBearerToken(getRestApiBearerToken()), []);
  useContextChannel(ctxTenant, (s) => {
    if (open && tab === 'preview' && s && s.status === 'FILLED') refresh(agentId);
  });

  // Load the pool's agent roster for the dropdown (same call connectors uses).
  useEffect(() => {
    if (!open || !networkId || !poolId) return;
    let cancelled = false;
    listAgentConnectorOverrides(networkId, poolId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.agents) ? res.agents : [];
        setAgents(rows);
      })
      .catch(() => { if (!cancelled) setAgents([]); });
    return () => { cancelled = true; };
  }, [open, networkId, poolId]);

  useEffect(() => {
    if (open) refresh(agentId);
  }, [open, agentId, refresh]);

  const onAgentChange = useCallback((next) => {
    setAgentId(next);
    setRetrieval(null); // a different agent ⇒ a different index; stale hits would mislead
    setRetrievalError(null);
  }, []);

  const runRetrieval = useCallback(async () => {
    if (!networkId) return;
    setRetrieving(true);
    setRetrievalError(null);
    try {
      const resp = await runContextRetrieval(networkId, {
        pool: poolId,
        agent: agentId || undefined,
        query: prompt,
      });
      setRetrieval(resp);
      if (resp?.error) setRetrievalError(resp.error);
    } catch (e) {
      setRetrievalError(e?.message || 'Vector retrieval failed');
    } finally {
      setRetrieving(false);
    }
  }, [networkId, poolId, agentId, prompt]);

  const rules = data?.rules || [];
  const dropped = data?.rulesDropped || [];
  const suppressed = data?.rulesDuplicatesSuppressed || [];   // §26.6 dedup — same content, not re-inserted
  const skills = data?.skills || [];
  const reference = data?.reference || [];
  const budget = data?.budgetChars;
  const used = data?.usedChars || 0;
  const pct = budget ? Math.min(100, Math.round((used / budget) * 100)) : 0;

  // ---- Level status + peek derivations (unified across the three levels) ----
  // Level 1 RULES — always-injected; ready as soon as the preview loads.
  const rulesChars = rules.reduce((n, b) => n + String(b?.text || '').length, 0);
  const rulesStatus = loading ? 'loading' : rules.length ? 'ready' : 'empty';
  const rulesPeek = rules.length
    ? `${rules.length} block${rules.length === 1 ? '' : 's'} · ${rulesChars.toLocaleString()} chars`
    : '';

  // SKILLS — per-agent distilled skills, injected alongside RULES (§2.6b). Only meaningful
  // when an agent is chosen (skills tag on the agent); shown verbatim as the distillate the
  // worker sees. `injected` mirrors the runtime (held / over-budget skills are NOT injected).
  const skillsInjected = skills.filter((s) => s.injected);
  const skillsHeld = skills.filter((s) => s.held);
  const skillsBudget = data?.skillsBudgetChars;
  const skillsUsed = data?.skillsUsedChars || 0;
  const skillsPct = skillsBudget ? Math.min(100, Math.round((skillsUsed / skillsBudget) * 100)) : 0;

  // TOTAL always-injected context = RULES + SKILLS (both go into every call; REFERENCE/Dynamic are
  // retrieved per-run, so they're excluded from the standing total). ``~tokens`` uses the common
  // ~4-chars/token heuristic so the user sees the standing input cost at a glance.
  const injectedChars = (used || rulesChars) + (skillsUsed || 0);
  const approxTokens = Math.round(injectedChars / 4);
  const skillsStatus = loading
    ? 'loading'
    : !agentId || !skillsInjected.length
      ? 'empty'
      : 'ready';
  const skillsPeek = !agentId
    ? 'pick an agent — skills are per-agent'
    : skillsInjected.length
      ? `${skillsInjected.length} skill${skillsInjected.length === 1 ? '' : 's'} · ${skillsUsed.toLocaleString()} chars${skillsHeld.length ? ` · ${skillsHeld.length} held` : ''}`
      : (skills.length ? `${skills.length} tagged · none injected` : '');

  // Level 2 REFERENCE — vector RAG; blank until a Run populates ``retrieval.hits``.
  const refHits = retrieval?.hits || [];
  const refStatus = retrieving
    ? 'loading'
    : !retrieval
      ? 'await-trigger'
      : refHits.length
        ? 'ready'
        : 'empty';
  const refPeek = retrieval
    ? (refHits.length ? `${refHits.length} chunk${refHits.length === 1 ? '' : 's'} retrieved` : 'no chunks above threshold')
    : (reference.length ? `${reference.length} source${reference.length === 1 ? '' : 's'} mounted — Run to retrieve` : '');

  // Level 3 Dynamic — pool memory; blank until a Run resolves a thread.
  const dyn = retrieval?.dynamic || null;
  const dynStatus = retrieving
    ? 'loading'
    : !retrieval
      ? 'await-trigger'
      : dyn && dyn.hasMemory
        ? 'ready'
        : 'empty';
  const dynPeek = dyn
    ? (dyn.hasMemory ? `thread “${dyn.threadId}” · matched by ${dyn.source}` : `thread “${dyn.threadId}” · no prior memory`)
    : '';

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="ctx-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30"
          />
          <motion.aside
            key="ctx-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-white shadow-xl sm:w-[40vw] sm:min-w-[480px]"
            data-testid="context-preview-panel"
          >
            <header className="flex items-start justify-between gap-2 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-gray-900">
                  Context{roomLabel ? ` · ${roomLabel}` : ''}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  {tab === 'preview'
                    ? 'Exactly the context the agent receives for this room — assembled by the same merge the runtime uses.'
                    : 'The whole-room status, built from cross-run pool memory.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close context preview"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18" strokeLinecap="round" />
                  <path d="M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </header>

            {/* Tab bar: per-agent Context preview | whole-room Room Context */}
            <div className="flex shrink-0 gap-1 border-b border-gray-100 px-3 pt-2" role="tablist">
              {[
                ['preview', 'Context preview'],
                ['room', 'Room Context'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  data-testid={`ctx-tab-${key}`}
                  className={`rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    tab === key
                      ? 'border-b-2 border-violet-500 text-violet-700'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'room' ? (
              <RoomContextTab open={open && tab === 'room'} networkId={networkId} poolId={poolId} />
            ) : (
            <>
            {/* Levels stack — scrolls; the composer is bottom-anchored below it. */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {error ? (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">
                  {error}
                </div>
              ) : null}

              {/* Standing input cost — RULES + SKILLS (always injected). REFERENCE/Dynamic are
                  retrieved per-run, so they're not in this total. ~tokens ≈ chars / 4. */}
              <div
                className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2"
                data-testid="ctx-total-cost"
              >
                <span className="text-[11px] font-medium text-indigo-700">Always-injected context</span>
                <span className="text-[11px] text-indigo-900">
                  <span className="font-semibold">{injectedChars.toLocaleString()}</span> chars ·
                  {' ~'}<span className="font-semibold">{approxTokens.toLocaleString()}</span> input tokens
                  <span className="ml-1 text-[10px] text-indigo-400">(RULES + SKILLS)</span>
                </span>
              </div>

              <div className="space-y-2" data-testid="ctx-levels">
                {/* Level 1 · RULES — always injected (verbatim, governed). */}
                <ContextLevelSection
                  level={1}
                  name="RULES"
                  subtitle="always injected"
                  status={rulesStatus}
                  peek={rulesPeek}
                  testId="ctx-level-rules"
                >
                  {rules.length === 0 ? (
                    <p className="text-[11px] text-gray-500">
                      No RULES apply to this room. Tag a file as RULES in the Context tab.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {budget ? (
                        <div>
                          <div className="flex items-center justify-between text-[10px] text-gray-500">
                            <span>budget</span>
                            <span>{used.toLocaleString()} / {budget.toLocaleString()} chars</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100">
                            <div
                              className={`h-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                      {/* One collapsible header PER doc (all collapsed); the body renders lazily via
                          the shared Markdown viewer only when the user opens it. */}
                      {rules.map((b, i) => (
                        <DocDisclosure
                          key={`r-${i}`}
                          testId={`ctx-rules-doc-${i}`}
                          title={b.filename || b.label || targetLabel({ scope: b.scope, networkId, poolId, agentName: agentId })}
                          chip={<ScopeTagChip scope={b.scope} networkId={networkId} poolId={poolId} agentName={agentId} />}
                          meta={`${String(b.text || '').length.toLocaleString()} chars`}
                        >
                          <MarkdownRenderer content={b.text || ''} />
                        </DocDisclosure>
                      ))}
                      {dropped.length > 0 ? (
                        <div>
                          <h4 className="text-[11px] font-semibold text-amber-700">Dropped at run time (over budget)</h4>
                          <ul className="mt-1 space-y-1">
                            {dropped.map((d, i) => (
                              <li key={`d-${i}`} className="text-[11px] text-amber-700">
                                <span className="font-medium">{d.label}</span>{d.filename ? ` · ${d.filename}` : ''} — {d.reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {suppressed.length > 0 ? (
                        <div data-testid="ctx-rules-suppressed">
                          <h4 className="text-[11px] font-semibold text-sky-700">
                            Duplicate content — injected once, not duplicated
                          </h4>
                          <ul className="mt-1 space-y-1">
                            {suppressed.map((d, i) => (
                              <li key={`sup-${i}`} className="text-[11px] text-sky-700">
                                <span className="font-medium">{d.filename || '(file)'}</span>
                                {' — same content as '}
                                <span className="font-medium">{d.duplicateOf || 'an already-injected file'}</span>
                                {', so it was not inserted again.'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  )}
                </ContextLevelSection>

                {/* Level 1.5 · SKILLS — per-agent distilled skills, always injected (§2.6b). */}
                <ContextLevelSection
                  level={'S'}
                  name="SKILLS"
                  subtitle="distilled · always injected"
                  status={skillsStatus}
                  peek={skillsPeek}
                  testId="ctx-level-skills"
                >
                  {!agentId ? (
                    <p className="text-[11px] text-gray-500">
                      Skills are per-agent — pick an agent below to see its injected skills.
                    </p>
                  ) : skills.length === 0 ? (
                    <p className="text-[11px] text-gray-500">
                      No skills tagged to this agent. Tag a file as SKILL in the Context tab.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {skillsBudget ? (
                        <div>
                          <div className="flex items-center justify-between text-[10px] text-gray-500">
                            <span>skills budget</span>
                            <span>{skillsUsed.toLocaleString()} / {skillsBudget.toLocaleString()} chars</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100">
                            <div
                              className={`h-full ${skillsPct >= 100 ? 'bg-red-500' : skillsPct >= 80 ? 'bg-amber-500' : 'bg-fuchsia-500'}`}
                              style={{ width: `${skillsPct}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                      {skills.map((s, i) => (
                        <DocDisclosure
                          key={`sk-${s.skillId || i}`}
                          testId={`ctx-skill-${i}`}
                          muted={!s.injected}
                          title={s.name}
                          chip={(
                            <span className="inline-flex items-center gap-1 rounded-full ring-1 ring-fuchsia-200 bg-fuchsia-50 px-2 py-0.5 text-[11px] font-medium text-fuchsia-700">
                              SKILL
                            </span>
                          )}
                          meta={`${(s.chars || 0).toLocaleString()} chars`}
                        >
                          {s.injected ? (
                            <MarkdownRenderer content={s.text || ''} />
                          ) : (
                            <p className="text-[11px] text-amber-700">
                              Not injected — {s.droppedReason || 'unavailable this run'}.
                            </p>
                          )}
                        </DocDisclosure>
                      ))}
                      <p className="text-[10px] text-gray-400">
                        Shown verbatim as the <span className="font-medium">distilled</span> brief the
                        worker receives (compressed from the source skill file), not the full skill.
                      </p>
                    </div>
                  )}
                </ContextLevelSection>

                {/* Level 2 · REFERENCE — vector RAG; populated by a Run. */}
                <ContextLevelSection
                  level={2}
                  name="REFERENCE"
                  subtitle="vector DB · retrieved on Run"
                  status={refStatus}
                  peek={refPeek}
                  testId="ctx-level-reference"
                >
                  <div data-testid="ctx-retrieval-results">
                    {retrievalError ? (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">
                        {retrievalError}
                      </div>
                    ) : refHits.length === 0 ? (
                      <p className="text-[11px] text-gray-500">
                        No chunks matched above the relevance threshold for this prompt.
                      </p>
                    ) : (
                      <>
                        {retrieval?.usedNeutralProbe ? (
                          <p className="mb-1 text-[10px] text-gray-400">representative sample — no prompt</p>
                        ) : retrieval?.query ? (
                          <p className="mb-1 text-[10px] text-gray-400">for “{retrieval.query}”</p>
                        ) : null}
                        <ul className="space-y-2">
                          {refHits.map((h, i) => (
                            <li key={`h-${i}`} className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-100">
                              <div className="flex items-baseline justify-between gap-2">
                                <span className="truncate text-[10px] font-medium text-gray-500">
                                  {h.sourceFile || h.chunkId || `chunk ${i + 1}`}
                                </span>
                                {typeof h.score === 'number' ? (
                                  <span className="shrink-0 rounded bg-violet-100 px-1.5 py-px text-[9px] font-semibold text-violet-700">
                                    {(h.score * 100).toFixed(0)}% match
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-700">
                                {h.text}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </ContextLevelSection>

                {/* Level 3 · Dynamic — ticket/keyword pool memory; populated by a Run. */}
                <ContextLevelSection
                  level={3}
                  name="Dynamic"
                  subtitle="pool memory · resolved on Run"
                  status={dynStatus}
                  peek={dynPeek}
                  testId="ctx-level-dynamic"
                >
                  <div data-testid="ctx-dynamic">
                    {dyn?.hasMemory ? (
                      <>
                        <p className="mb-1 text-[10px] text-gray-400">
                          thread “{dyn.threadId}” · matched by {dyn.source}
                        </p>
                        <div className="max-h-72 overflow-auto rounded-md bg-teal-50 px-3 py-2 text-[12px] leading-relaxed text-gray-800 ring-1 ring-teal-100">
                          <MarkdownRenderer content={dyn.preamble || ''} />
                        </div>
                      </>
                    ) : (
                      <p className="text-[11px] text-gray-500">
                        No prior memory on thread “{dyn?.threadId}” yet — the first run seeds it.
                      </p>
                    )}
                  </div>
                </ContextLevelSection>
              </div>
            </div>

            {/* Bottom-anchored composer — agent picker + Run live inside the pill. */}
            <div className="shrink-0 border-t border-gray-100 px-4 py-3">
              <PromptComposer
                value={prompt}
                onChange={setPrompt}
                onRun={runRetrieval}
                running={retrieving}
                allowEmpty
                agents={agents}
                agentId={agentId}
                onAgentChange={onAgentChange}
                allAgentsLabel="All agents"
                placeholder="Ask what this agent would retrieve… (blank = representative sample)"
                runLabel="Run"
                testId="ctx-composer"
                hint="Runs the real vector + pool-memory path the runtime uses — populates Level 2 & 3."
              />
            </div>
            </>
            )}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
